import type {ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import {ChevronRight, ExternalLink, Send} from 'lucide-react';
import {cn} from '@chekinapp/ui';
import {useVelaChat} from '../hooks/useVelaChat';
import styles from './VelaChat.module.css';

export type VelaChatQuestion = {
  id: string;
  question: ReactNode;
  answer: ReactNode;
  chipClassName?: string;
};

type Props = {
  questions: VelaChatQuestion[];
  onAsk?: (id: string) => void;
  variant?: 'chips' | 'rows';
};

function VelaChat({questions, onAsk, variant = 'chips'}: Props) {
  const isRows = variant === 'rows';
  const {t} = useTranslation('vela');
  const {currentId, turn, ask} = useVelaChat({
    ids: questions.map(question => question.id),
  });
  const current = questions.find(question => question.id === currentId);

  if (!current) return null;

  const handleAsk = (id: string) => {
    if (id === currentId) return;
    ask(id);
    onAsk?.(id);
  };

  return (
    <div className={cn(styles.chat, 'flex flex-col gap-2', isRows && 'gap-2.5 pt-5')}>
      <div aria-live="polite">
        <div
          key={turn}
          className={cn(
            "flex flex-col gap-1.5 [[data-fit~='1']_&]:gap-1",
            isRows && 'gap-2.5',
            turn > 0 &&
              'duration-[250ms] animate-in fade-in-0 slide-in-from-bottom-1 motion-reduce:animate-none',
          )}
        >
          <p
            className={cn(
              'max-w-[85%] self-end rounded-[14px_14px_4px_14px] px-3 py-2 text-[14px] font-medium leading-[18px]',
              isRows
                ? 'rounded-[18px_18px_4px_18px] bg-[color-mix(in_srgb,var(--chekin-color-brand-navy)_78%,white)] px-3.5 py-2.5 font-semibold leading-5 text-[var(--chekin-color-white)] [box-shadow:none]'
                : 'bg-[var(--vela-chat-question-bg)] text-[var(--vela-chat-question-text)] [box-shadow:var(--vela-chat-question-shadow)] backdrop-blur-md backdrop-saturate-150',
              "[[data-fit~='3']_&]:leading-[17px]",
            )}
          >
            {current.question}
          </p>
          <div
            className={cn(
              'rounded-[4px_14px_14px_14px] bg-[var(--vela-chat-answer-bg)] px-3 py-[9px] text-[12px] leading-[17px] text-[var(--vela-chat-answer-text)]',
              isRows &&
                'rounded-[18px_18px_18px_4px] bg-[var(--chekin-color-white)] px-3.5 py-[11px] text-[14px] leading-[1.5] text-[var(--chekin-color-brand-navy)]',
              "[[data-fit~='3']_&]:leading-4",
            )}
          >
            {current.answer}
          </div>
        </div>
      </div>

      <span
        className={cn(
          'mt-1 text-[12px] font-extrabold uppercase leading-4 tracking-[0.13em] text-[var(--vela-chat-label-text)]',
          isRows && 'mt-2.5 tracking-[0.08em] text-[var(--chekin-neutral-600)]',
        )}
      >
        {t('chat.suggested')}
      </span>

      <div
        className={cn(
          "flex flex-col items-start gap-1.5 [[data-fit~='2']_&]:gap-1",
          isRows && 'items-stretch gap-2',
        )}
      >
        {questions.map(question => {
          const isCurrent = question.id === currentId;

          if (isRows) {
            return (
              <button
                key={question.id}
                type="button"
                aria-current={isCurrent ? 'true' : undefined}
                onClick={() => handleAsk(question.id)}
                className={cn(
                  'flex items-center gap-2.5 rounded-[14px] bg-[var(--chekin-color-white)] p-3.5 text-left text-[14px] font-bold leading-5 text-[var(--chekin-color-brand-navy)] transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--assistant-section-accent)]',
                  isCurrent &&
                    'shadow-[inset_0_0_0_1.5px_var(--chekin-color-brand-blue)]',
                )}
              >
                <span className="min-w-0 flex-1">{question.question}</span>
                <span
                  aria-hidden="true"
                  className={cn(
                    'grid size-[30px] shrink-0 place-items-center rounded-full [&_svg]:size-[15px]',
                    isCurrent
                      ? 'bg-[var(--chekin-color-brand-blue)] text-[var(--chekin-color-white)]'
                      : 'bg-[var(--chekin-blue-surface-100)] text-[var(--chekin-color-brand-blue)]',
                  )}
                >
                  <Send />
                </span>
              </button>
            );
          }

          return (
            <button
              key={question.id}
              type="button"
              aria-current={isCurrent ? 'true' : undefined}
              onClick={() => handleAsk(question.id)}
              className={cn(
                'rounded-[var(--button-radius)] bg-[var(--chekin-neutral-50)] px-2.5 py-1.5 text-left text-[12px] font-semibold leading-4 text-[var(--assistant-tip-title)] transition-colors',
                'hover:bg-[var(--vela-chat-chip-hover-bg)]',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--assistant-section-accent)] focus-visible:ring-offset-1',
                "[[data-fit~='1']_&]:py-[5px]",
                "[[data-fit~='2']_&]:px-[9px] [[data-fit~='2']_&]:py-1",
                "[[data-fit~='3']_&]:py-[3px] [[data-fit~='3']_&]:leading-[15px]",
                isCurrent &&
                  'bg-[var(--vela-chat-chip-active-bg)] text-[var(--vela-chat-chip-active-text)] hover:bg-[var(--vela-chat-chip-active-bg)]',
                question.chipClassName,
              )}
            >
              {question.question}
            </button>
          );
        })}
      </div>
    </div>
  );
}

type LinkProps = {
  children: ReactNode;
  href?: string;
  onClick?: () => void;
};

const LINK_CLASS = cn(
  'mt-1.5 flex w-fit items-center gap-0.5 rounded-sm text-left text-[12px] font-semibold leading-4 text-[var(--assistant-section-accent)] hover:underline',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--assistant-section-accent)] [&_svg]:size-3.5 [&_svg]:flex-none',
);

function VelaChatLink({children, href, onClick}: LinkProps) {
  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={LINK_CLASS}>
        {children}
        <ExternalLink />
      </a>
    );
  }

  return (
    <button type="button" onClick={onClick} className={LINK_CLASS}>
      {children}
      <ChevronRight />
    </button>
  );
}

export {VelaChat, VelaChatLink};
